import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const initialState = {
  accounts: [],
  roles: [],
  units: [],
  positions: [],
  modules: [],
  selectedRoleDetail: null,
  total: 0,
  page: 1,
  pageSize: 10,
  sortColumn: 'username',
  sortDirection: 'asc',
  filterUsername: '',
  filterRoles: [],
  filterUnits: [],
  loading: false,
  error: null,
  successMessage: null,
  selectedAccount: null,
  // OTP Login state
  otpLoginData: null,
  otpLoginLoading: false,
  otpLoginError: null,
};

const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    // Action to trigger fetch init data (saga will handle this)
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.roles = action.payload.roles || [];
      state.units = action.payload.units || [];
      state.positions = action.payload.positions || [];
      state.modules = action.payload.modules || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch accounts (saga will handle this)
    fetchAccounts: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAccountsSuccess: (state, action) => {
      state.loading = false;
      state.accounts = action.payload.data || [];
      // Extract pagination info from meta object
      state.total = action.payload.meta?.total || 0;
      state.page = action.payload.meta?.page || 1;
      state.pageSize = action.payload.meta?.size || 10;
      state.error = null;
    },
    fetchAccountsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger add account
    addAccount: (state) => {
      state.loading = true;
      state.error = null;
    },
    addAccountSuccess: (state, action) => {
      state.loading = false;
      state.accounts.push(action.payload);
      state.error = null;
      state.successMessage = i18n.t('accounts.success.created');
    },
    addAccountFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch single account
    fetchAccountById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAccountByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedAccount = action.payload;
      state.error = null;
    },
    fetchAccountByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch role by ID (for permissions display)
    fetchRoleById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchRoleByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedRoleDetail = action.payload;
      state.error = null;
    },
    fetchRoleByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Clear selected role detail
    clearSelectedRoleDetail: (state) => {
      state.selectedRoleDetail = null;
    },
    // Action to trigger update account
    updateAccount: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateAccountSuccess: (state, action) => {
      state.loading = false;
      const index = state.accounts.findIndex((acc) => acc.id === action.payload.id);
      if (index !== -1) {
        state.accounts[index] = action.payload;
      }
      state.error = null;
      state.successMessage = i18n.t('accounts.success.updated');
    },
    updateAccountFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger delete account
    deleteAccount: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteAccountSuccess: (state, action) => {
      state.loading = false;
      state.accounts = state.accounts.filter((acc) => acc.id !== action.payload);
      state.error = null;
      state.successMessage = i18n.t('accounts.success.deleted');
    },
    deleteAccountFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger bulk delete accounts
    bulkDeleteAccounts: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteAccountsSuccess: (state, action) => {
      state.loading = false;
      state.accounts = state.accounts.filter((acc) => !action.payload.includes(acc.id));
      state.error = null;
      state.successMessage = i18n.t('accounts.success.bulkDeleted', { count: action.payload.length });
    },
    bulkDeleteAccountsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Set selected account
    setSelectedAccount: (state, action) => {
      state.selectedAccount = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    // Set pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page when page size changes
    },
    // Set sort
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    // Set filter
    setFilterUsername: (state, action) => {
      state.filterUsername = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },
    setFilterRoles: (state, action) => {
      state.filterRoles = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },
    setFilterUnits: (state, action) => {
      state.filterUnits = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },
    // Request OTP Login
    requestOtpLogin: (state) => {
      state.otpLoginLoading = true;
      state.otpLoginError = null;
      state.otpLoginData = null;
    },
    requestOtpLoginSuccess: (state, action) => {
      state.otpLoginLoading = false;
      state.otpLoginData = action.payload;
      state.otpLoginError = null;
    },
    requestOtpLoginFailure: (state, action) => {
      state.otpLoginLoading = false;
      state.otpLoginError = action.payload;
      state.otpLoginData = null;
    },
    // Clear OTP Login data
    clearOtpLoginData: (state) => {
      state.otpLoginData = null;
      state.otpLoginError = null;
      state.otpLoginLoading = false;
    },
  },
});

// Export actions
export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchAccounts,
  fetchAccountsSuccess,
  fetchAccountsFailure,
  fetchAccountById,
  fetchAccountByIdSuccess,
  fetchAccountByIdFailure,
  fetchRoleById,
  fetchRoleByIdSuccess,
  fetchRoleByIdFailure,
  clearSelectedRoleDetail,
  addAccount,
  addAccountSuccess,
  addAccountFailure,
  updateAccount,
  updateAccountSuccess,
  updateAccountFailure,
  deleteAccount,
  deleteAccountSuccess,
  deleteAccountFailure,
  bulkDeleteAccounts,
  bulkDeleteAccountsSuccess,
  bulkDeleteAccountsFailure,
  setSelectedAccount,
  clearError,
  clearSuccessMessage,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterUsername,
  setFilterRoles,
  setFilterUnits,
  requestOtpLogin,
  requestOtpLoginSuccess,
  requestOtpLoginFailure,
  clearOtpLoginData,
} = accountsSlice.actions;

// Selectors
export const selectAccounts = (state) => state.accounts.accounts;
export const selectRoles = (state) => state.accounts.roles;
export const selectUnits = (state) => state.accounts.units;
export const selectPositions = (state) => state.accounts.positions || [];
export const selectModules = (state) => state.accounts.modules;
export const selectSelectedRoleDetail = (state) => state.accounts.selectedRoleDetail;
export const selectAccountsLoading = (state) => state.accounts.loading;
export const selectAccountsError = (state) => state.accounts.error;
export const selectAccountsSuccessMessage = (state) => state.accounts.successMessage;
export const selectSelectedAccount = (state) => state.accounts.selectedAccount;
export const selectAccountsTotal = (state) => state.accounts.total;
export const selectAccountsPage = (state) => state.accounts.page;
export const selectAccountsPageSize = (state) => state.accounts.pageSize;
export const selectAccountsSortColumn = (state) => state.accounts.sortColumn;
export const selectAccountsSortDirection = (state) => state.accounts.sortDirection;
export const selectAccountsFilterUsername = (state) => state.accounts.filterUsername;
export const selectAccountsFilterRoles = (state) => state.accounts.filterRoles;
export const selectAccountsFilterUnits = (state) => state.accounts.filterUnits;
export const selectOtpLoginData = (state) => state.accounts.otpLoginData;
export const selectOtpLoginLoading = (state) => state.accounts.otpLoginLoading;
export const selectOtpLoginError = (state) => state.accounts.otpLoginError;

// Export reducer
export default accountsSlice.reducer;
